/**
 * Email Notification Service
 * Triggers email notifications for all EMS and Nomination activities.
 * Uses GraphQLEmailService to send emails via the Lambda SMTP sender.
 */

import { GraphQLEmailService } from './graphqlEmail';
import { mergeNominationRecipients } from './nominationSettings';

export const APP_URL = window.location.origin;

/*
 * Where the email logo is fetched from — by the RECIPIENT's mail client, not by this browser.
 *
 * So it has to be a public HTTPS address. REACT_APP_EMAIL_LOGO_URL names it (the same asset the
 * backend's COMPANY_LOGO_URL points at). Without it, the portal's own copy is used when the portal
 * is itself public HTTPS. From localhost or a LAN address no mail client can reach the file, and
 * the previous fallback — a CloudFront address — no longer exists, which is what put a broken
 * image in every email sent from a developer machine. There, the logo is left out and the header
 * keeps the company name, rather than showing a broken box.
 */
const origin = window.location.origin;
const isPublicOrigin =
  origin.startsWith('https://') &&
  !/localhost|127\.0\.0\.1|192\.168\.|10\.\d+\.|172\.(1[6-9]|2\d|3[01])\./.test(origin);

const LOGO_URL =
  process.env.REACT_APP_EMAIL_LOGO_URL || (isPublicOrigin ? `${origin}/ci-logo.png` : '');

/** The logo's display size, from ci-logo.png's own 493 x 723. Outlook needs both as attributes. */
const LOGO_HEIGHT = 40;
const LOGO_WIDTH = Math.round((493 / 723) * LOGO_HEIGHT);

/**
 * Wrapper that sends email only if not in demo mode.
 * Silently fails - email is best-effort, should not block UI.
 */
export async function sendEmailSafe(to: string[], subject: string, htmlBody: string) {
  const emailsDisabled = process.env.REACT_APP_DISABLE_EMAILS === 'true';
  if (emailsDisabled) {
    console.log('[EmailNotification] Skipping email because REACT_APP_DISABLE_EMAILS=true:', subject);
    return;
  }

  const recipients = Array.from(new Set(to.map(email => email.trim().toLowerCase()).filter(Boolean)));
  if (recipients.length === 0) {
    console.warn('[EmailNotification] No recipients for email:', subject);
    return;
  }

  try {
    console.log('[EmailNotification] Sending email:', { to: recipients, subject });
    const result = await GraphQLEmailService.sendEmail({ to: recipients, subject, htmlBody });
    if (!result.success) {
      console.warn('[EmailNotification] Email not sent:', result.message);
    } else {
      console.log('[EmailNotification] Email sent:', result.messageId || result.message);
    }
  } catch (error) {
    console.warn('[EmailNotification] Email send failed (non-blocking):', error);
  }
}

/** Branded email wrapper — forces light-mode rendering to prevent
 *  Outlook / Gmail / Apple Mail dark-mode color inversion.            */
export function wrapEmail(title: string, body: string, statusColor: string = '#0d8a6a'): string {
  return `
    <!DOCTYPE html>
    <html xmlns="http://www.w3.org/1999/xhtml" lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <!--[if mso]><meta http-equiv="X-UA-Compatible" content="IE=edge"><![endif]-->
      <meta name="color-scheme" content="light only">
      <meta name="supported-color-schemes" content="light only">
      <title>${title}</title>
      <style>
        :root { color-scheme: light only; supported-color-schemes: light only; }
        body, table, td, p, a, span, div, h1, h2, h3, strong {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
        }
        /* Force light mode in Outlook desktop dark mode */
        [data-ogsc] body,
        [data-ogsc] table,
        [data-ogsc] td,
        [data-ogsc] p,
        [data-ogsc] span,
        [data-ogsc] div,
        [data-ogsc] a,
        [data-ogsc] strong {
          color: inherit !important;
        }
        [data-ogsb] body,
        [data-ogsb] table,
        [data-ogsb] td { background-color: inherit !important; }
      </style>
    </head>
    <body style="margin: 0; padding: 0; background-color: #f3f4f6; font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937;">
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f3f4f6; padding: 20px 0;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" role="presentation" style="max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <!-- Header with Logo and Company Name -->
              <tr>
                <td style="background: linear-gradient(135deg, #0d8a6a, #065f46); background-color: #0d8a6a; padding: 20px 28px;">
                  <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                    <tr>
                      ${LOGO_URL ? `<td align="left" style="vertical-align: middle;">
                        <img src="${LOGO_URL}" width="${LOGO_WIDTH}" height="${LOGO_HEIGHT}" alt="Creyente InfoTech" style="display: block; border: 0; height: ${LOGO_HEIGHT}px; width: ${LOGO_WIDTH}px;" />
                      </td>` : ''}
                      <td align="right" style="vertical-align: middle;">
                        <span style="color: #ffffff; font-size: 24px; font-weight: 700; font-family: 'Segoe UI', Arial, sans-serif;">Creyente InfoTech</span>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <!-- Title Bar -->
              <tr>
                <td style="background-color: ${statusColor}; border-top: 1px solid rgba(255,255,255,0.1); padding: 16px 28px;">
                  <h1 style="color: #ffffff; margin: 0; font-size: 20px; font-weight: 600; text-align: center;">${title}</h1>
                </td>
              </tr>
              <!-- Content -->
              <tr>
                <td style="padding: 32px 28px; background-color: #ffffff; color: #1f2937;">
                  ${body}
                </td>
              </tr>
              <!-- Footer -->
              <tr>
                <td style="padding: 20px 28px; background-color: #f1f5f9; text-align: center; color: #64748b;">
                  <p style="color: #64748b; font-size: 12px; margin: 0; line-height: 1.5;">
                    This is an automated notification from CreyenteOne EMS.<br/>
                    Please do not reply to this email.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
}

export function actionButton(label: string, url: string): string {
  return `
    <table width="100%" border="0" cellpadding="0" cellspacing="0" style="margin: 24px 0;">
      <tr>
        <td align="center">
          <table border="0" cellpadding="0" cellspacing="0">
            <tr>
              <td align="center" bgcolor="#0d8a6a" style="border-radius: 8px; background-color: #0d8a6a;">
                <a href="${url}" target="_blank" style="display: inline-block; padding: 14px 32px; color: #ffffff !important; text-decoration: none; font-weight: 600; font-size: 15px; font-family: 'Segoe UI', Arial, sans-serif;">
                  ${label}
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
}

// ─── EXPENSE EMAIL NOTIFICATIONS ───

/** 1. Employee submits expense → email manager */
export async function emailExpenseSubmitted(
  managerEmail: string,
  employeeName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) {
  const html = wrapEmail('📋 New Expense Submitted', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      <strong style="color: #111827;">${employeeName}</strong> has submitted a new expense claim for your review.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Amount:</td>
        <td style="color: #0d8a6a; font-weight: 700; font-size: 18px;">₹${amount.toLocaleString()}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Submitted by:</td>
        <td style="color: #1e293b; font-size: 14px;">${employeeName}</td>
      </tr>
    </table>
    ${actionButton('Review Expense', `${APP_URL}/manager`)}
  `, '#f59e0b'); // Pending review color (orange)
  await sendEmailSafe([managerEmail], `New Expense: ${expenseTitle} - ₹${amount.toLocaleString()}`, html);
}

/** 2. Manager approves expense → email employee + finance admins */
export async function emailExpenseManagerApproved(
  employeeEmail: string,
  financeAdminEmails: string[],
  managerName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) {
  // Email to employee
  const empHtml = wrapEmail('✅ Expense Approved by Manager', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your expense <strong style="color: #111827;">"${expenseTitle}"</strong> has been approved by <strong style="color: #111827;">${managerName}</strong> and forwarded to Finance for final approval.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Amount:</td>
        <td style="color: #0d8a6a; font-weight: 700; font-size: 18px;">₹${amount.toLocaleString()}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Approved by:</td>
        <td style="color: #1e293b; font-size: 14px;">${managerName}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Status:</td>
        <td style="color: #0d8a6a; font-weight: 600; font-size: 14px;">✅ Forwarded to Finance</td>
      </tr>
    </table>
    ${actionButton('View Expense', `${APP_URL}/dashboard`)}
  `);
  await sendEmailSafe([employeeEmail], `Expense Approved: ${expenseTitle}`, empHtml);

  // Email to finance admins
  if (financeAdminEmails.length > 0) {
    const finHtml = wrapEmail('💰 Expense Pending Finance Approval', `
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
        An expense has been approved by the manager and requires your finance review.
      </p>
      <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
        <tr>
          <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
          <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Amount:</td>
          <td style="color: #0d8a6a; font-weight: 700; font-size: 18px;">₹${amount.toLocaleString()}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Approved by:</td>
          <td style="color: #1e293b; font-size: 14px;">${managerName}</td>
        </tr>
      </table>
      ${actionButton('Review in Finance Dashboard', `${APP_URL}/admin`)}
    `);
    await sendEmailSafe(financeAdminEmails, `Finance Review: ${expenseTitle} - ₹${amount.toLocaleString()}`, finHtml);
  }
}

/** 3. Manager rejects expense → email employee */
export async function emailExpenseManagerRejected(
  employeeEmail: string,
  managerName: string,
  expenseTitle: string,
  expenseId: string,
  reason?: string
) {
  const html = wrapEmail('❌ Expense Rejected', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your expense <strong style="color: #111827;">"${expenseTitle}"</strong> has been rejected by <strong style="color: #111827;">${managerName}</strong>.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Rejected by:</td>
        <td style="color: #1e293b; font-size: 14px;">${managerName}</td>
      </tr>
    </table>
    ${reason ? `<div style="background: #fef2f2; border-left: 4px solid #ef4444; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;"><p style="color: #991b1b; margin: 0; font-size: 14px;"><strong>Reason:</strong> ${reason}</p></div>` : ''}
    ${actionButton('View Details', `${APP_URL}/dashboard`)}
  `, '#dc2626');
  await sendEmailSafe([employeeEmail], `Expense Rejected: ${expenseTitle}`, html);
}

/** 4. Finance approves expense → email employee */
export async function emailExpenseFinanceApproved(
  employeeEmail: string,
  financeAdminName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) {
  const html = wrapEmail('✅ Expense Finance Approved', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your expense <strong style="color: #111827;">"${expenseTitle}"</strong> has received finance approval from <strong style="color: #111827;">${financeAdminName}</strong>.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Amount:</td>
        <td style="color: #0d8a6a; font-weight: 700; font-size: 18px;">₹${amount.toLocaleString()}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Approved by:</td>
        <td style="color: #1e293b; font-size: 14px;">${financeAdminName} (Finance)</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Status:</td>
        <td style="color: #0d8a6a !important; font-weight: 600; font-size: 14px;">✅ Approved — Reimbursement pending</td>
      </tr>
    </table>
    ${actionButton('View Expense', `${APP_URL}/dashboard`)}
  `);
  await sendEmailSafe([employeeEmail], `Finance Approved: ${expenseTitle} - ₹${amount.toLocaleString()}`, html);
}

/** 5. Finance rejects expense → email employee */
export async function emailExpenseFinanceRejected(
  employeeEmail: string,
  financeAdminName: string,
  expenseTitle: string,
  expenseId: string,
  reason?: string
) {
  const html = wrapEmail('❌ Expense Rejected by Finance', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your expense <strong style="color: #111827;">"${expenseTitle}"</strong> has been rejected by Finance (<strong style="color: #111827;">${financeAdminName}</strong>).
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Rejected by:</td>
        <td style="color: #1e293b; font-size: 14px;">${financeAdminName} (Finance)</td>
      </tr>
    </table>
    ${reason ? `<div style="background: #fef2f2; border-left: 4px solid #ef4444; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;"><p style="color: #991b1b; margin: 0; font-size: 14px;"><strong>Reason:</strong> ${reason}</p></div>` : ''}
    ${actionButton('View Details', `${APP_URL}/dashboard`)}
  `, '#dc2626');
  await sendEmailSafe([employeeEmail], `Finance Rejected: ${expenseTitle}`, html);
}

/** 6. Expense reimbursed → email employee */
export async function emailExpenseReimbursed(
  employeeEmail: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) {
  const html = wrapEmail('💸 Expense Reimbursed', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your expense <strong style="color: #111827;">"${expenseTitle}"</strong> has been reimbursed.
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 24px 0;">
      <tr>
        <td align="center">
          <div style="background: #e6faf4; border: 2px solid #0d8a6a; border-radius: 12px; padding: 32px; text-align: center;">
            <p style="color: #0d8a6a !important; font-size: 14px; margin: 0 0 12px 0; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;">Amount Reimbursed</p>
            <p style="color: #065f46 !important; font-size: 36px; font-weight: 800; margin: 0;">₹${amount.toLocaleString()}</p>
          </div>
        </td>
      </tr>
    </table>
    <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 20px 0 0 0;">
      The amount has been processed for reimbursement and will be credited to your account as per the company's reimbursement schedule.
    </p>
    ${actionButton('View Details', `${APP_URL}/dashboard`)}
  `, '#0d8a6a'); // Reimbursed color (green)
  await sendEmailSafe([employeeEmail], `Reimbursed: ${expenseTitle} - ₹${amount.toLocaleString()}`, html);
}

/** 6b. Employee deletes expense → email manager */
export async function emailExpenseDeleted(
  managerEmail: string,
  employeeName: string,
  expenseTitle: string,
  amount: number
) {
  const html = wrapEmail('🗑️ Expense Deleted', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      <strong style="color: #111827;">${employeeName}</strong> has deleted an expense that was previously submitted for your review.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Expense:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${expenseTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Amount:</td>
        <td style="color: #dc2626; font-weight: 700; font-size: 18px;">₹${amount.toLocaleString()}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Deleted by:</td>
        <td style="color: #1e293b; font-size: 14px;">${employeeName}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Status:</td>
        <td style="color: #dc2626; font-weight: 600; font-size: 14px;">🗑️ Deleted</td>
      </tr>
    </table>
    <p style="color: #4b5563; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
      No further action is required. This expense has been permanently removed.
    </p>
    ${actionButton('View Dashboard', `${APP_URL}/manager`)}
  `, '#dc2626'); // Red for deletion
  await sendEmailSafe([managerEmail], `Expense Deleted: ${expenseTitle} - ₹${amount.toLocaleString()}`, html);
}

// ─── NOMINATION EMAIL NOTIFICATIONS ───

/** 7. Nomination submitted → email reviewers and super admins */
export async function emailNominationSubmitted(
  reviewerEmails: string[],
  adminEmails: string[],
  nominatorName: string,
  nomineeName: string,
  awardType: string,
  nominationId: string
) {
  const allEmails = mergeNominationRecipients([...reviewerEmails, ...adminEmails], { includeReceiver: true, includeCc: true });
  if (allEmails.length === 0) return;

  const html = wrapEmail('⭐ New Nomination Submitted', `
    <div style="padding: 0;">
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
        A new award nomination has been submitted and requires your review.
      </p>
      <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
        <tr>
          <td style="color: #64748b; width: 140px; font-size: 14px;">Nominee/Team:</td>
          <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${nomineeName}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Award Type:</td>
          <td style="color: #0d8a6a; font-weight: 600; font-size: 14px;">${awardType}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Nominated by:</td>
          <td style="color: #1e293b; font-size: 14px;">${nominatorName}</td>
        </tr>
      </table>
    </div>
    ${actionButton('Review Nomination', `${APP_URL}/nominations/review`)}
  `, '#f59e0b'); // Pending review color (orange)
  await sendEmailSafe(allEmails, `New Nomination: ${nomineeName} for ${awardType}`, html);
}

/** 8. Nomination under review → email nominator */
export async function emailNominationUnderReview(
  nominatorEmail: string,
  reviewerName: string,
  nomineeName: string,
  nominationId: string
) {
  const html = wrapEmail('🔍 Nomination Under Review', `
    <div style="padding: 0;">
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0;">
        Your nomination for <strong style="color: #111827;">${nomineeName}</strong> is now being reviewed by <strong style="color: #111827;">${reviewerName}</strong>.
      </p>
    </div>
    ${actionButton('View Nomination', `${APP_URL}/nominations/${nominationId}`)}
  `, '#f59e0b'); // Under review color (orange)
  await sendEmailSafe([nominatorEmail], `Nomination Under Review: ${nomineeName}`, html);
}

/** 9. Nomination approved → email nominator + admins */
export async function emailNominationApproved(
  nominatorEmail: string,
  adminEmails: string[],
  reviewerName: string,
  nomineeName: string,
  awardType: string,
  nominationId: string
) {
  // Email nominator - using the table layout to match the first photo
  const nomHtml = wrapEmail('🏆 Nomination Approved', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      Your award nomination has been approved!
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Award Nominee/Team:</td>
        <td style="color: #1e293b; font-weight: 700; font-size: 14px;">${nomineeName}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Award Type:</td>
        <td style="color: #0d8a6a; font-weight: 700; font-size: 14px;">${awardType}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Approved by:</td>
        <td style="color: #1e293b; font-size: 14px;">${reviewerName}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Status:</td>
        <td style="color: #0d8a6a; font-weight: 600; font-size: 14px;">🏆 Approved — Pending Publication</td>
      </tr>
    </table>
    <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 0;">
      The nomination is now pending publication by an admin. Once published, it will appear on the recognition feed for all employees to see.
    </p>
    ${actionButton('View Nomination', `${APP_URL}/nominations/${nominationId}`)}
  `, '#0d8a6a'); // Approved color (green)
  await sendEmailSafe([nominatorEmail], `Nomination Approved: ${nomineeName} - ${awardType}`, nomHtml);

  // Email admins
  if (adminEmails.length > 0) {
    const adminHtml = wrapEmail('📢 Nomination Ready to Publish', `
      <div style="padding: 0;">
        <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
          A nomination has been approved and is ready for publication.
        </p>
        <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
          <tr>
            <td style="color: #64748b; width: 140px; font-size: 14px;">Nominee/Team:</td>
            <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${nomineeName}</td>
          </tr>
          <tr>
            <td style="color: #64748b; font-size: 14px;">Award:</td>
            <td style="color: #0d8a6a; font-weight: 600; font-size: 14px;">${awardType}</td>
          </tr>
          <tr>
            <td style="color: #64748b; font-size: 14px;">Approved by:</td>
            <td style="color: #1e293b; font-size: 14px;">${reviewerName}</td>
          </tr>
        </table>
      </div>
      ${actionButton('Publish Nomination', `${APP_URL}/nominations/admin`)}
    `, '#f59e0b'); // Ready to publish color (orange)
    await sendEmailSafe(mergeNominationRecipients(adminEmails, { includeReceiver: true }), `Ready to Publish: ${nomineeName} - ${awardType}`, adminHtml);
  }
}

/** 10. Nomination rejected → email nominator */
export async function emailNominationRejected(
  nominatorEmail: string,
  reviewerName: string,
  nomineeName: string,
  nominationId: string,
  reason?: string
) {
  const html = wrapEmail('❌ Nomination Rejected', `
    <div style="padding: 0;">
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
        Your nomination for <strong style="color: #111827;">${nomineeName}</strong> has been rejected by <strong style="color: #111827;">${reviewerName}</strong>.
      </p>
      ${reason ? `<div style="background: #fef2f2; border-left: 4px solid #ef4444; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;"><p style="color: #991b1b; margin: 0; font-size: 14px;"><strong>Reason:</strong> ${reason}</p></div>` : ''}
    </div>
    ${actionButton('View Details', `${APP_URL}/nominations/${nominationId}`)}
  `, '#dc2626');
  await sendEmailSafe([nominatorEmail], `Nomination Rejected: ${nomineeName}`, html);
}

/** 11. Nomination published → email all employees */
export async function emailNominationPublished(
  allEmployeeEmails: string[],
  nomineeName: string,
  awardType: string,
  publisherName: string,
  nomineeEmail?: string
) {
  if (allEmployeeEmails.length === 0) return;

  // Send special email to the nominee
  if (nomineeEmail) {
    const nomineeHtml = wrapEmail('🏆 You Have Been Recognized!', `
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
        Congratulations! We are thrilled to let you know that you have been recognized by your team.
      </p>
      <table width="100%" cellpadding="0" cellspacing="0" style="margin: 24px 0;">
        <tr>
          <td align="center">
            <div style="background: linear-gradient(135deg, #fef3c7, #fde68a); border: 2px solid #f59e0b; border-radius: 12px; padding: 32px; text-align: center;">
              <p style="color: #92400e; font-size: 14px; margin: 0 0 8px 0; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">Award</p>
              <p style="color: #78350f; font-size: 28px; font-weight: 800; margin: 0;">🏆 ${awardType}</p>
            </div>
          </td>
        </tr>
      </table>
      <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
        <tr>
          <td style="color: #64748b; width: 140px; font-size: 14px;">Recipient:</td>
          <td style="color: #1e293b; font-weight: 700; font-size: 14px;">${nomineeName}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Award Type:</td>
          <td style="color: #0d8a6a; font-weight: 700; font-size: 14px;">${awardType}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Published by:</td>
          <td style="color: #1e293b; font-size: 14px;">${publisherName}</td>
        </tr>
      </table>
      <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 20px 0 0 0;">
        Your hard work and dedication have been noticed. Visit the recognition feed to see the full details and celebrate with your colleagues!
      </p>
      ${actionButton('View Your Recognition', `${APP_URL}/nominations/feed`)}
    `, '#f59e0b');
    await sendEmailSafe([nomineeEmail], `🏆 Congratulations! You've been recognized - ${awardType}`, nomineeHtml);
  }

  // Send general email to all other employees
  const otherEmails = nomineeEmail
    ? allEmployeeEmails.filter(e => e.toLowerCase() !== nomineeEmail.toLowerCase())
    : allEmployeeEmails;

  if (otherEmails.length > 0) {
    const html = wrapEmail('🎉 Recognition Published', `
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
        Exciting news! A new award recognition has been published to the company feed.
      </p>
      <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
        <tr>
          <td style="color: #64748b; width: 140px; font-size: 14px;">Nominee/Team:</td>
          <td style="color: #1e293b; font-weight: 700; font-size: 14px;">${nomineeName}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Award Type:</td>
          <td style="color: #0d8a6a; font-weight: 700; font-size: 14px;">${awardType}</td>
        </tr>
        <tr>
          <td style="color: #64748b; font-size: 14px;">Published by:</td>
          <td style="color: #1e293b; font-size: 14px;">${publisherName}</td>
        </tr>
      </table>
      <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 0;">
        Check out the recognition feed to congratulate your colleague and see their accomplishments!
      </p>
      ${actionButton('Open Recognition Feed', `${APP_URL}/nominations/feed`)}
    `);
    await sendEmailSafe(otherEmails, `🎉 Recognition: ${nomineeName} - ${awardType}`, html);
  }
}

/** 12. Comment on nomination → email nominator */
export async function emailNominationComment(
  nominatorEmail: string,
  commenterName: string,
  nomineeName: string,
  nominationId: string
) {
  const html = wrapEmail('💬 New Comment on Nomination', `
    <div style="padding: 0;">
      <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0;">
        <strong style="color: #111827;">${commenterName}</strong> commented on the recognition for <strong style="color: #111827;">${nomineeName}</strong>.
      </p>
    </div>
    ${actionButton('View Comment', `${APP_URL}/nominations/feed`)}
  `);
  await sendEmailSafe([nominatorEmail], `New comment on ${nomineeName}'s recognition`, html);
}

// ─── VOTING POLL EMAIL NOTIFICATIONS ───

/** 13. Voter added to poll → email new voter */
export async function emailVoterAdded(
  voterEmails: string[],
  pollTitle: string,
  addedByName: string,
  pollId: string
) {
  if (voterEmails.length === 0) return;

  const html = wrapEmail('🗳️ Added to Voting Poll', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      You have been added as a voter to the poll <strong style="color: #111827;">"${pollTitle}"</strong> by <strong style="color: #111827;">${addedByName}</strong>.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Poll:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${pollTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Added by:</td>
        <td style="color: #1e293b; font-size: 14px;">${addedByName}</td>
      </tr>
    </table>
    <div style="background: #f0fdf4; border-left: 4px solid #16a34a; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
      <p style="color: #166534; margin: 0; font-size: 14px;">
        <strong>Action Required:</strong> Your vote is needed. Please cast your vote at your earliest convenience.
      </p>
    </div>
    ${actionButton('Cast Your Vote', `${APP_URL}/voting/${pollId}`)}
  `, '#0d8a6a'); // Use teal for voting invitations
  await sendEmailSafe(voterEmails, `🗳️ You've been added to voting poll: ${pollTitle}`, html);
}

/** 14. Voter removed from poll → email removed voter */
export async function emailVoterRemoved(
  voterEmails: string[],
  pollTitle: string,
  removedByName: string
) {
  if (voterEmails.length === 0) return;

  const html = wrapEmail('🗳️ Removed from Voting Poll', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      You have been removed from the voting poll <strong style="color: #111827;">"${pollTitle}"</strong> by <strong style="color: #111827;">${removedByName}</strong>.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Poll:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${pollTitle}</td>
      </tr>
      <tr>
        <td style="color: #64748b; font-size: 14px;">Removed by:</td>
        <td style="color: #1e293b; font-size: 14px;">${removedByName}</td>
      </tr>
    </table>
    <div style="background: #fffbeb; border-left: 4px solid #f59e0b; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
      <p style="color: #92400e; margin: 0; font-size: 14px;">
        <strong>Note:</strong> You no longer need to vote on this poll. If you believe this was a mistake, please contact the poll creator.
      </p>
    </div>
    ${actionButton('View Voting Polls', `${APP_URL}/voting`)}
  `, '#0d8a6a'); // Use teal for consistency
  await sendEmailSafe(voterEmails, `Removed from voting poll: ${pollTitle}`, html);
}

/** 15. New voting poll created → email all invited voters */
export async function emailPollCreated(
  voterEmails: string[],
  pollTitle: string,
  createdByName: string,
  nomineeNames: string[],
  deadline?: string,
  pollId?: string,
  description?: string
) {
  if (voterEmails.length === 0) return;

  const html = wrapEmail('🗳️ You\'ve Been Invited to Vote', `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      <strong style="color: #111827;">${createdByName}</strong> has created a voting poll and needs your input.
    </p>
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">Poll:</td>
        <td style="color: #1e293b; font-weight: 600; font-size: 14px;">${pollTitle}</td>
      </tr>
      ${description ? `
      <tr>
        <td style="color: #64748b; font-size: 14px;">Description:</td>
        <td style="color: #1e293b; font-size: 14px;">${description}</td>
      </tr>` : ''}
      <tr>
        <td style="color: #64748b; font-size: 14px;">Nominees:</td>
        <td style="color: #1e293b; font-size: 14px;">${nomineeNames.join(', ')}</td>
      </tr>
      ${deadline ? `
      <tr>
        <td style="color: #64748b; font-size: 14px;">Deadline:</td>
        <td style="color: #1e293b; font-size: 14px;">${new Date(deadline).toLocaleDateString()}</td>
      </tr>` : ''}
    </table>
    <div style="background: #f0fdf4; border-left: 4px solid #16a34a; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
      <p style="color: #166534; margin: 0; font-size: 14px;">
        <strong>Action Required:</strong> Please log in to the portal to cast your vote.
      </p>
    </div>
    ${pollId ? actionButton('Cast Your Vote', `${APP_URL}/voting/${pollId}`) : ''}
  `, '#0d8a6a'); // Use teal for voting invitations
  await sendEmailSafe(voterEmails, `Voting Poll: ${pollTitle}`, html);
}


// ═══════════════════════════════════════════════════════════════
// TIMESHEET EMAIL NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Get the timesheetCcEmail from saved config.
 * Falls back to hr@creyentetech.com if not configured.
 */
async function getTimesheetCcEmail(): Promise<string> {
  try {
    const config = await GraphQLEmailService.getEmailConfig();
    return config?.timesheetCcEmail || 'hr@creyentetech.com';
  } catch {
    return 'hr@creyentetech.com';
  }
}

/**
 * Direct email sender for timesheet notifications — bypasses demo mode check
 * since timesheets are always a production feature.
 */
async function sendTimesheetEmail(to: string[], subject: string, htmlBody: string) {
  console.log('[TimesheetEmail] ===== SENDING EMAIL =====');
  console.log('[TimesheetEmail] To:', to);
  console.log('[TimesheetEmail] Subject:', subject);
  try {
    const result = await GraphQLEmailService.sendEmail({ to, subject, htmlBody });
    console.log('[TimesheetEmail] Result:', JSON.stringify(result));
    if (result.success) {
      console.log('[TimesheetEmail] ✅ Email sent successfully');
    } else {
      console.error('[TimesheetEmail] ❌ Email failed:', result.message);
    }
    return result;
  } catch (error) {
    console.error('[TimesheetEmail] ❌ Exception:', error);
    return { success: false, message: String(error) };
  }
}

/** Timesheet Submitted → Email to employee (confirmation) + manager (action needed) + optional CC */
export async function emailTimesheetSubmitted(
  employeeEmail: string,
  employeeName: string,
  managerEmail: string,
  managerName: string,
  weekLabel: string,
  totalHours: number
) {
  const ccEmail = await getTimesheetCcEmail();
  const empTo = ccEmail ? [employeeEmail, ccEmail] : [employeeEmail];
  const mgrTo = ccEmail ? [managerEmail, ccEmail] : [managerEmail];

  // Email to employee (confirmation)
  const empHtml = wrapEmail('Timesheet Submitted', `
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Hi <strong>${employeeName}</strong>,
    </p>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Your timesheet for <strong>${weekLabel}</strong> has been submitted successfully to your manager <strong>${managerName}</strong>.
    </p>
    <div style="background: #f0fdf4; border: 1px solid #a7f3d0; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: #065f46; font-size: 14px;">
        <strong>Week:</strong> ${weekLabel}<br/>
        <strong>Total Hours:</strong> ${totalHours}h<br/>
        <strong>Status:</strong> Pending Approval<br/>
        <strong>Manager:</strong> ${managerName}
      </p>
    </div>
    <p style="color: #64748b; font-size: 13px;">
      You will be notified once your manager reviews the timesheet.
    </p>
    ${actionButton('View My Timesheets', `${APP_URL}/timesheet`)}
  `);
  await sendTimesheetEmail(empTo, `Timesheet Submitted: ${weekLabel} (${totalHours}h)`, empHtml);

  // Email to manager (action needed)
  const mgrHtml = wrapEmail('Timesheet Pending Approval', `
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Hi <strong>${managerName}</strong>,
    </p>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      <strong>${employeeName}</strong> has submitted their timesheet for your review.
    </p>
    <div style="background: #fef3c7; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: #92400e; font-size: 14px;">
        <strong>Employee:</strong> ${employeeName} (${employeeEmail})<br/>
        <strong>Week:</strong> ${weekLabel}<br/>
        <strong>Total Hours:</strong> ${totalHours}h<br/>
        <strong>Status:</strong> Awaiting Your Approval
      </p>
    </div>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Please review and approve or return the timesheet.
    </p>
    ${actionButton('Review Timesheet', `${APP_URL}/timesheet/team`)}
  `, '#f59e0b');
  await sendTimesheetEmail(mgrTo, `⏰ Timesheet Pending: ${employeeName} - ${weekLabel} (${totalHours}h)`, mgrHtml);
}

/** Timesheet Approved → Email to employee + optional CC */
export async function emailTimesheetApproved(
  employeeEmail: string,
  employeeName: string,
  managerName: string,
  weekLabel: string,
  totalHours: number
) {
  const ccEmail = await getTimesheetCcEmail();
  const to = ccEmail ? [employeeEmail, ccEmail] : [employeeEmail];

  const html = wrapEmail('Timesheet Approved', `
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Hi <strong>${employeeName}</strong>,
    </p>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Great news! Your timesheet has been <strong style="color: #065f46;">approved</strong> by <strong>${managerName}</strong>.
    </p>
    <div style="background: #f0fdf4; border: 1px solid #a7f3d0; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: #065f46; font-size: 14px;">
        <strong>Week:</strong> ${weekLabel}<br/>
        <strong>Total Hours:</strong> ${totalHours}h<br/>
        <strong>Status:</strong> ✓ Approved<br/>
        <strong>Approved by:</strong> ${managerName}
      </p>
    </div>
    ${actionButton('View My Timesheets', `${APP_URL}/timesheet`)}
  `, '#0d8a6a');
  await sendTimesheetEmail(to, `✓ Timesheet Approved: ${weekLabel} (${totalHours}h)`, html);
}

/** Timesheet Returned → Email to employee + optional CC */
export async function emailTimesheetReturned(
  employeeEmail: string,
  employeeName: string,
  managerName: string,
  weekLabel: string,
  totalHours: number,
  reason: string
) {
  const ccEmail = await getTimesheetCcEmail();
  const to = ccEmail ? [employeeEmail, ccEmail] : [employeeEmail];

  const html = wrapEmail('Timesheet Returned', `
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Hi <strong>${employeeName}</strong>,
    </p>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Your timesheet has been <strong style="color: #dc2626;">returned</strong> by <strong>${managerName}</strong> for revision.
    </p>
    <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: #991b1b; font-size: 14px;">
        <strong>Week:</strong> ${weekLabel}<br/>
        <strong>Total Hours:</strong> ${totalHours}h<br/>
        <strong>Status:</strong> Returned<br/>
        <strong>Reason:</strong> ${reason}
      </p>
    </div>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      Please review the feedback and resubmit your timesheet.
    </p>
    ${actionButton('Edit My Timesheet', `${APP_URL}/timesheet`)}
  `, '#dc2626');
  await sendTimesheetEmail(to, `↩ Timesheet Returned: ${weekLabel} - Action Required`, html);
}



/*
 * ─── Leave Roster ─────────────────────────────────────────────────────
 *
 * The leave emails live in leaveEmails.ts. They moved out because every one of them has to
 * reach the same HR address on top of its own recipients, and a rule that has to hold across
 * five messages is better expressed once in a file that holds all five than repeated in a
 * file that also holds expenses, nominations, polls, timesheets and regularizations.
 *
 * The chrome they render with — wrapEmail, actionButton, sendEmailSafe, APP_URL — is still
 * imported from here, so a leave email looks like every other mail the product sends.
 */

/**
 * Everyone holding an HR or Super Admin role, by email.
 *
 * Exported rather than private because two unrelated flows need it: leaveEmails.ts copies HR on
 * every leave message, and the regularization below routes to HR when an employee has no
 * reporting manager. One query, one definition of "HR", so the two cannot come to disagree
 * about who that is.
 *
 * Role-derived, and therefore a moving target — which is exactly why the leave flow pairs it
 * with a fixed address rather than trusting it alone. See HR_LEAVE_EMAIL.
 */
export async function getHRAdminEmails(): Promise<string[]> {
  try {
    const { generateClient } = await import('aws-amplify/api');
    const gqlClient = generateClient();
    const listUsersQuery = `query ListUsers($limit: Int) { listUsers(limit: $limit) { items { email role } } }`;
    const result = await gqlClient.graphql({ query: listUsersQuery, variables: { limit: 200 }, authMode: 'userPool' } as any) as any;
    const allUsers = result.data?.listUsers?.items || [];
    return allUsers
      .filter((u: any) => u.role === 'HR_Admin' || u.role === 'Super_Admin')
      .map((u: any) => u.email?.toLowerCase())
      .filter(Boolean);
  } catch (error) {
    console.warn('[EmailNotification] Failed to fetch HR admin emails:', error);
    return [];
  }
}

/* ─── Attendance regularization ──────────────────────────────────────────────────────────
 *
 * An employee asks for a day to be put right; their reporting manager decides. Two messages:
 * one to the approver when it is raised, one to the employee when it is answered.
 *
 * Both go through sendEmailSafe, which never throws — §18 requires that a failed email cannot
 * roll back an approval, and reusing the existing helper is what makes that true rather than
 * something each caller has to remember.
 */

/** "09:58" → "09:58 AM"; anything missing reads as an em dash rather than an empty cell. */
function clock12(value?: string): string {
  if (!value) return '—';
  const [h, m] = value.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return value;
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * A regularization request was raised → tell the approver.
 *
 * Sent to the reporting manager. When the employee has none the request is routed to HR
 * instead, and this sends there — an incomplete reporting line must not leave a request sitting
 * in a queue nobody is told about.
 */
export async function emailRegularizationSubmitted(input: {
  employeeName: string;
  employeeEmail: string;
  employeeCode?: string;
  managerEmail?: string;
  attendanceDate: string;
  currentIn?: string;
  currentOut?: string;
  currentStatus?: string;
  requestedIn?: string;
  requestedOut?: string;
  reason: string;
}) {
  const details = `
    <div style="background: #fef3c7; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: #92400e; font-size: 14px;">
        <strong>Employee:</strong> ${input.employeeName} (${input.employeeEmail})<br/>
        <strong>Employee Code:</strong> ${input.employeeCode || 'Not set'}<br/>
        <strong>Attendance Date:</strong> ${input.attendanceDate}<br/>
        <strong>Current IN:</strong> ${clock12(input.currentIn)}<br/>
        <strong>Current OUT:</strong> ${clock12(input.currentOut)}<br/>
        <strong>Current Status:</strong> ${input.currentStatus || '—'}<br/>
        <strong>Requested IN:</strong> ${clock12(input.requestedIn)}<br/>
        <strong>Requested OUT:</strong> ${clock12(input.requestedOut)}<br/>
        <strong>Reason:</strong> ${input.reason}
      </p>
    </div>`;

  const approverUrl = `${APP_URL}/attendance/regularization`;

  if (input.managerEmail) {
    const html = wrapEmail('🕒 Attendance Regularization Request', `
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">Hi,</p>
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
        <strong>${input.employeeName}</strong> has asked for their attendance on
        <strong>${input.attendanceDate}</strong> to be regularized. Attendance is unchanged until
        you approve it.
      </p>
      ${details}
      ${actionButton('Review Request', approverUrl)}
    `, '#f59e0b');

    await sendEmailSafe(
      [input.managerEmail],
      `Attendance Regularization Request — ${input.employeeName} — ${input.attendanceDate}`,
      html
    );
    return;
  }

  /*
   * No reporting manager, so HR decides.
   *
   * Said plainly in the email rather than silently: the missing reporting line is itself
   * something HR would want to fix, and the request is only in their queue because of it.
   */
  const hrAdminEmails = await getHRAdminEmails();
  if (hrAdminEmails.length === 0) return;

  const html = wrapEmail('🕒 Attendance Regularization Request (No Manager Assigned)', `
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">Hi HR Team,</p>
    <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
      <strong>${input.employeeName}</strong> has asked for their attendance on
      <strong>${input.attendanceDate}</strong> to be regularized. They have no reporting manager
      set in Employee Master, so this request has come to you.
    </p>
    ${details}
    ${actionButton('Review Request', approverUrl)}
  `, '#f59e0b');

  await sendEmailSafe(
    hrAdminEmails,
    `Attendance Regularization Request — ${input.employeeName} — ${input.attendanceDate}`,
    html
  );
}

/**
 * The request was decided → tell the employee.
 *
 * A rejection always carries the approver's comment when there is one. "Rejected" with no
 * reason is the most frustrating message an employee can receive, and it guarantees a follow-up
 * conversation that the comment field exists to avoid.
 */
export async function emailRegularizationDecision(input: {
  employeeName: string;
  employeeEmail: string;
  attendanceDate: string;
  approved: boolean;
  decidedByName: string;
  managerComment?: string;
  appliedIn?: string;
  appliedOut?: string;
}) {
  const { approved } = input;

  const body = approved
    ? `
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">Hi ${input.employeeName},</p>
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
        Your attendance regularization request for <strong>${input.attendanceDate}</strong> has
        been <strong>approved</strong>. The requested attendance has been applied.
      </p>
      <div style="background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <p style="margin: 0; color: #065f46; font-size: 14px;">
          <strong>Approved by:</strong> ${input.decidedByName}<br/>
          <strong>IN:</strong> ${clock12(input.appliedIn)}<br/>
          <strong>OUT:</strong> ${clock12(input.appliedOut)}
          ${input.managerComment ? `<br/><strong>Comment:</strong> ${input.managerComment}` : ''}
        </p>
      </div>`
    : `
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">Hi ${input.employeeName},</p>
      <p style="color: #1f2937; font-size: 15px; line-height: 1.6;">
        Your attendance regularization request for <strong>${input.attendanceDate}</strong> has
        been <strong>rejected</strong>. Your attendance for that date is unchanged.
      </p>
      <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <p style="margin: 0; color: #991b1b; font-size: 14px;">
          <strong>Reviewed by:</strong> ${input.decidedByName}
          ${input.managerComment ? `<br/><strong>Comment:</strong> ${input.managerComment}` : ''}
        </p>
      </div>`;

  const html = wrapEmail(
    approved ? '✅ Regularization Approved' : '❌ Regularization Rejected',
    `${body}${actionButton('View Attendance', `${APP_URL}/attendance/my-attendance`)}`,
    approved ? '#0d8a6a' : '#dc2626'
  );

  await sendEmailSafe(
    [input.employeeEmail],
    `Attendance Regularization ${approved ? 'Approved' : 'Rejected'} — ${input.attendanceDate}`,
    html
  );
}
